import React, { useCallback, useMemo } from 'react';
import { Dropdown } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

import {
    TPO_CONSTRAINT_ADD_LEVEL_TEST_ID,
    TPO_OPTIMIZE_LEVEL_SELECT,
    tpoOptionTestId,
} from '../../../../config/testIds';
import type { Level } from './types';

interface Props {
    constraintName: string;
    options: { key: Level; label: string; disabled: boolean }[];
    onSelect: (constraintName: string, level: Level) => void;
    addLabel: string;
    exhaustedLabel: string;
}

const TRIGGER_ON: ('click' | 'hover' | 'contextMenu')[] = ['click'];

const AddLevelControl: React.FC<Props> = ({ constraintName, options, onSelect, addLabel, exhaustedLabel }) => {
    const handleClick = useCallback<NonNullable<MenuProps['onClick']>>(
        ({ key }) => onSelect(constraintName, key),
        [constraintName, onSelect],
    );

    const items = useMemo<MenuProps['items']>(
        () =>
            options.map((option) => ({
                key: option.key,
                label: option.label,
                disabled: option.disabled,
                'data-testid': tpoOptionTestId(TPO_OPTIMIZE_LEVEL_SELECT, option.key),
            })),
        [options],
    );

    const triggerTestId = TPO_CONSTRAINT_ADD_LEVEL_TEST_ID(constraintName);

    // Every level is already on this KPI, so there is nothing left to open.
    if (options.length === 0) {
        return (
            <span
                className="inline-flex items-center gap-1.5 text-sm text-[rgba(0,0,0,0.25)] select-none"
                data-testid={triggerTestId}
                data-state-disabled="true"
            >
                <PlusCircleOutlined />
                {exhaustedLabel}
            </span>
        );
    }

    return (
        <Dropdown menu={{ items, onClick: handleClick }} trigger={TRIGGER_ON} placement="bottomLeft">
            <button
                type="button"
                data-testid={triggerTestId}
                className="inline-flex items-center gap-1.5 text-sm text-[rgba(0,0,0,0.65)] hover:text-[#44338a] transition-colors cursor-pointer"
            >
                <PlusCircleOutlined />
                {addLabel}
            </button>
        </Dropdown>
    );
};

AddLevelControl.displayName = 'AddLevelControl';
export default React.memo(AddLevelControl);
